import { type FC, useCallback, useEffect, useRef } from "react";
import { Navigate, Outlet, generatePath, matchPath, useLocation, useParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { AbsoluteRoute, RouteEntities } from "../../../core/routes";
import { defaultOrganizationContainerSelector } from "../../../core/recoil/organizations";
import { PageContentSpinner } from "../../../layout/components";
import { useOrgChildResources, usePageRedirect, useSelectedOrganizationContainer } from "../../../shared/hooks";
import { useOrganizationsV2 } from "../../../shared/hooks/useOrganizationsV2";
import { CAMERAKIT_FEATURES, Feature } from "../../../shared/models";
import { useEula } from "../../eula/hooks/useEula";
import { OrganizationSelection } from "../../onboarding/components/OrganizationSelection";
import { useOnboardingOrganization } from "../../onboarding/hooks/useOnboardingOrganizations";
import { useOrganizationSwitcher } from "../../organizations/hooks/useOrganizationSwitcher";
import { CamkitUnauthorizedCard } from "./CamkitUnauthorizedCard";
import { useFeatureGates } from "../../../cof-feature-gates/hooks";
import { CofConfigId, ResourceKind } from "../../../gql-generated/types";

const getCamkitFeatureWithRedirects = (pathname: string): Feature => {
    const matchesLensSchedulerRoot = matchPath({ path: AbsoluteRoute.CK_LENS_SCHEDULER, end: false }, pathname);
    const matchesAppsRoot = matchPath({ path: AbsoluteRoute.CK_APPS, end: false }, pathname);
    // lens-schedulers or apps - under root
    const matchesLensScheduler = matchPath({ path: AbsoluteRoute.ORG_LENS_SCHEDULER, end: false }, pathname);
    const matchesApps = matchPath({ path: AbsoluteRoute.ORG_APPS, end: false }, pathname);

    if (matchesLensScheduler || matchesLensSchedulerRoot) {
        return Feature.lensScheduler;
    }

    if (matchesApps || matchesAppsRoot) {
        return Feature.apps;
    }

    return Feature.cameraKit;
};

export const CamkitOutletWithRedirect: FC = () => {
    const { pathname, search } = useLocation();

    const { orgId } = useParams<{ orgId: string }>();
    const { shouldRedirectToEula, loading: isEulaLoading } = useEula();
    const isEulaPage = pathname.includes(RouteEntities.Eula);
    const isEulaRedirect = shouldRedirectToEula && !isEulaPage;

    const defaultOrganizationContainerV1 = useRecoilValue(defaultOrganizationContainerSelector);
    const { container } = useSelectedOrganizationContainer();

    const { configs } = useFeatureGates();
    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const { defaultOrganizationContainer: defaultOrganizationContainerV2 } = useOrganizationsV2();

    const defaultOrganizationContainer = isMyLensesOrgsV2Enabled
        ? defaultOrganizationContainerV2
        : defaultOrganizationContainerV1;

    const { resourceContainers: cameraKitResources, loading: isCameraKitResourcesLoading } = useOrgChildResources({
        kind: ResourceKind.CameraKits,
    });

    const hasFeatures = (c: typeof container): c is Extract<typeof container, { features: unknown }> => {
        return c !== undefined && "features" in c;
    };

    const isCameraKitEnabled = isMyLensesOrgsV2Enabled
        ? cameraKitResources.length > 0
        : hasFeatures(container) && CAMERAKIT_FEATURES.every((ft) => container.features.has(ft));
    const isValidOrg = container !== undefined;

    const redirect = usePageRedirect(getCamkitFeatureWithRedirects(pathname)); // lens-scheduler and apps - need org id
    const { loading: isOrgLoading } = useOrganizationSwitcher(orgId);

    const { enableOrg, isEnabling, enableError } = useOnboardingOrganization();

    // useMutation errors persist across renders, so gating on !enableError would block onboarding
    // a *different* org after a failed attempt. Tracking the attempted orgId here scopes the
    // no-retry rule to the failing org only and prevents the effect's re-fires from looping.
    const lastAttemptedOrgIdRef = useRef<string | null>(null);

    const onboardOrgToCameraKit = useCallback(async () => {
        if (
            isValidOrg &&
            !isCameraKitResourcesLoading &&
            !isCameraKitEnabled &&
            !isEnabling &&
            orgId &&
            lastAttemptedOrgIdRef.current !== orgId
        ) {
            lastAttemptedOrgIdRef.current = orgId;
            await enableOrg({
                variables: {
                    orgId,
                    token: "",
                },
            });
        }
    }, [enableOrg, isCameraKitEnabled, isCameraKitResourcesLoading, isEnabling, isValidOrg, orgId]);

    useEffect(() => {
        onboardOrgToCameraKit();
    }, [onboardOrgToCameraKit]);

    if (isEulaLoading || isOrgLoading || (isMyLensesOrgsV2Enabled && isCameraKitResourcesLoading)) {
        return <PageContentSpinner />;
    }

    if (isEulaRedirect) {
        return (
            <Navigate
                to={`${generatePath(AbsoluteRoute.CK_EULA)}?redirect=${encodeURIComponent(`${pathname}${search}`)}`}
            />
        );
    }

    if (redirect) {
        return <Navigate to={redirect} />;
    }

    if (!isEulaPage) {
        if (!isValidOrg) {
            if (defaultOrganizationContainer !== undefined) {
                return (
                    <Navigate
                        to={generatePath(AbsoluteRoute.ORG_APPS_CREATE, {
                            orgId: defaultOrganizationContainer.id,
                        })}
                    />
                );
            } else {
                return <OrganizationSelection />;
            }
        } else if (!isCameraKitEnabled && enableError) {
            return <CamkitUnauthorizedCard />;
        } else {
            return <Outlet />;
        }
    }

    return <Outlet />;
};
